import React, { useContext, useEffect, useState } from 'react';
import { 
  AppBar, 
  Toolbar, 
  Typography, 
  Button, 
  Box,
  IconButton,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Divider,
  ThemeProvider as MUIThemeProvider,
  useTheme,
  useMediaQuery,
  Drawer,
  List,
  ListItem,
  ListItemButton,
} from '@mui/material';
import { 
  AccountCircle,
  Settings,
  AdminPanelSettings,
  Logout,
  Groups as GroupsIcon,
  Menu as MenuIcon,
  Dashboard as DashboardIcon,
  Folder as FolderIcon,
  Book as BookIcon,
  ManageAccounts as ManageAccountsIcon
} from '@mui/icons-material';
import { FaHome } from 'react-icons/fa';
import { AuthContext } from '../context/AuthContext';
import { Link, useNavigate } from 'react-router-dom';
import { PERMISSIONS } from '../config/permissions';
import { USER_ROLES } from '../config/constants';
import { usePermissions } from '../hooks/usePermissions';
import { lightTheme } from '../theme';

export default function NavBar() {
  const { user, role, logout, loading } = useContext(AuthContext);
  const { hasPermission } = usePermissions();
  const navigate = useNavigate();
  const [anchorEl, setAnchorEl] = useState(null);
  const [mobileOpen, setMobileOpen] = useState(false);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  if (loading) return null;

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const handleLogout = async () => {
    try {
      setAnchorEl(null);
      setMobileOpen(false);
      const { error } = await logout();
      
      if (error) {
        throw error;
      }
      
      localStorage.removeItem('selectedProject');
      sessionStorage.clear(); 
      
      window.location.href = '/login'; 
    } catch (error) {
      console.error('Logout error:', error.message);
      alert('Er ging iets mis bij het uitloggen. Probeer het opnieuw.');
    }
  };

  const handleMenu = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleMenuItemClick = (path) => {
    handleClose();
    setMobileOpen(false);
    navigate(path);
  };

  const handleDrawerItemClick = (path) => {
    setMobileOpen(false);
    navigate(path);
  }

  const drawer = (
    <Box onClick={handleDrawerToggle} sx={{ textAlign: 'center', pt: 2 }}>
      <Typography variant="h6" sx={{ my: 2 }}>
        Menu
      </Typography>
      <Divider />
      <List>
        <ListItem disablePadding>
          <ListItemButton onClick={() => handleDrawerItemClick('/')}>
            <ListItemIcon>
              <DashboardIcon />
            </ListItemIcon>
            <ListItemText primary="Dashboard" />
          </ListItemButton>
        </ListItem>

        {hasPermission(PERMISSIONS.VIEW_PROJECT) && (
          <ListItem disablePadding>
            <ListItemButton onClick={() => handleDrawerItemClick('/projects')}>
              <ListItemIcon>
                <FolderIcon />
              </ListItemIcon>
              <ListItemText primary="Projecten" />
            </ListItemButton>
          </ListItem>
        )}

        {(role === USER_ROLES.ADMIN || role === USER_ROLES.PROJECT_MANAGER) && (
          <ListItem disablePadding>
            <ListItemButton onClick={() => handleDrawerItemClick('/library')}>
              <ListItemIcon>
                <BookIcon />
              </ListItemIcon>
              <ListItemText primary="Bibliotheek" />
            </ListItemButton>
          </ListItem>
        )}

        {(role === USER_ROLES.ADMIN || role === USER_ROLES.PROJECT_MANAGER) && (
          <ListItem disablePadding>
            <ListItemButton onClick={() => handleDrawerItemClick('/project-management')}>
              <ListItemIcon>
                <ManageAccountsIcon />
              </ListItemIcon>
              <ListItemText primary="Projectbeheer" />
            </ListItemButton>
          </ListItem>
        )}
      </List>
    </Box>
  );

  return (
    <MUIThemeProvider theme={lightTheme}>
      <AppBar 
        position="fixed"
        elevation={0}
        sx={{ 
          bgcolor: lightTheme.palette.primary.main, 
          color: lightTheme.palette.primary.contrastText,
          zIndex: (theme) => theme.zIndex.drawer + 1
        }}
      >
        <Toolbar>
          {isMobile ? (
            <>
              <IconButton
                color="inherit"
                aria-label="open drawer"
                edge="start"
                onClick={handleDrawerToggle}
                sx={{ mr: 2 }}
              >
                <MenuIcon />
              </IconButton>
              <IconButton
                color="inherit"
                component={Link}
                to="/"
                edge="start"
                sx={{ mr: 1 }}
              >
                <FaHome />
              </IconButton>
              <Typography 
                variant="h6" 
                sx={{ 
                  textDecoration: 'none',
                  display: { xs: 'none', sm: 'block' }
                }}
                component={Link}
                to="/"
                color="inherit"
              >
                Bodem-SaaS
              </Typography>
              <Box sx={{ flexGrow: 1 }} />
              <IconButton
                size="large"
                onClick={handleMenu}
                color="inherit"
                edge="end"
              >
                <AccountCircle />
              </IconButton>
            </>
          ) : (
            <>
              <Link to="/" style={{ color: 'inherit', display: 'flex', alignItems: 'center' }}>
                <FaHome style={{ marginRight: 8 }} />
              </Link>
              <Typography 
                variant="h6" 
                sx={{ 
                  flexGrow: 1,
                  textDecoration: 'none'
                }}
                component={Link}
                to="/"
                color="inherit"
              >
                Bodem-SaaS
              </Typography>
              <Button color="inherit" component={Link} to="/">
                Dashboard
              </Button>
              {hasPermission(PERMISSIONS.VIEW_PROJECT) && (
                <Button color="inherit" component={Link} to="/projects">
                  Projecten
                </Button>
              )}
              {(role === USER_ROLES.ADMIN || role === USER_ROLES.PROJECT_MANAGER) && (
                <Button color="inherit" component={Link} to="/library">
                  Bibliotheek
                </Button>
              )}
              {(role === USER_ROLES.ADMIN || role === USER_ROLES.PROJECT_MANAGER) && (
                <Button color="inherit" component={Link} to="/project-management">
                  Projectbeheer
                </Button>
              )}
              <IconButton
                size="large"
                onClick={handleMenu}
                color="inherit"
                edge="end"
              >
                <AccountCircle />
              </IconButton>
            </>
          )}
          <Menu
            id="menu-appbar"
            anchorEl={anchorEl}
            anchorOrigin={{
              vertical: 'bottom',
              horizontal: 'right',
            }}
            keepMounted
            transformOrigin={{
              vertical: 'top',
              horizontal: 'right',
            }}
            open={Boolean(anchorEl)}
            onClose={handleClose}
            sx={{ mt: '45px' }}
          >
            <MenuItem disabled>
              <Typography variant="body2" color="textSecondary">
                {user?.email}
              </Typography>
            </MenuItem>
            <Divider />
            <MenuItem onClick={() => handleMenuItemClick('/settings')}>
              <ListItemIcon>
                <Settings fontSize="small" />
              </ListItemIcon>
              <ListItemText>Instellingen</ListItemText>
            </MenuItem>
            {hasPermission(PERMISSIONS.MANAGE_EMPLOYEES) && (
              <MenuItem onClick={() => handleMenuItemClick('/employee-management')}>
                <ListItemIcon>
                  <GroupsIcon fontSize="small" />
                </ListItemIcon>
                <ListItemText>Werknemer Beheer</ListItemText>
              </MenuItem>
            )}
            {hasPermission(PERMISSIONS.MANAGE_USERS) && (
              <MenuItem onClick={() => handleMenuItemClick('/admin')}>
                <ListItemIcon>
                  <AdminPanelSettings fontSize="small" />
                </ListItemIcon>
                <ListItemText>Admin Panel</ListItemText>
              </MenuItem>
            )}
            <MenuItem onClick={handleLogout}>
              <ListItemIcon>
                <Logout fontSize="small" />
              </ListItemIcon>
              <ListItemText>Uitloggen</ListItemText>
            </MenuItem>
          </Menu>
        </Toolbar>
      </AppBar>
      <Box component="nav">
        <Drawer
          variant="temporary"
          open={mobileOpen}
          onClose={handleDrawerToggle}
          ModalProps={{
            keepMounted: true,
          }}
          sx={{
            display: { xs: 'block', md: 'none' },
            '& .MuiDrawer-paper': { boxSizing: 'border-box', width: 240 },
          }}
        >
          {drawer}
        </Drawer>
      </Box>
    </MUIThemeProvider>
  );
}
